import React, { useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Settings } from 'lucide-react';
import { colors } from '@annote/core/styles';
import { debounceWithFlush } from 'src/helpers/debounce';
import { useProfileContext } from 'src/context/ProfileContext';
import { useSettings } from 'src/context/SettingsContext';
import { usePanelContext } from 'src/context/PanelContext';
import { SETTINGS_CONFIG, SettingPath, SettingMetadata } from '@annote/settings';
import { ProfileSettingField } from 'src/components/Profile/ProfileSettingField';
import userImgUrl from 'src/routes/Settings/user.png';
import { PanelHeader } from './Panel';
import { PanelToggleButton } from './PanelToggleButton';

const Backdrop = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1999;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  transition: opacity 300ms ease-in-out;
`;

const ProfilePanelWrapper = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 48rem;
  height: 100vh;
  background: ${colors.bg.primary};
  box-shadow: ${colors.shadow.toolbar};
  z-index: 2000;
  transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '100%')});
  transition: transform 300ms ease-in-out;
  display: flex;
  flex-direction: column;
`;

const ProfileContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 3rem;
  padding: 3.125rem;
  padding-bottom: 6rem;
  background: ${colors.bg.primary};
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const Header = styled.span`
  font-size: 2.5rem;
  font-weight: 700;
  line-height: 3rem;
`;

const UserDetails = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid rgba(200, 202, 203, 1);
  border-radius: 0.75rem;
  padding: 1rem;
  flex: 1;
`;

const EmailGroup = styled.div`
  display: flex;
  gap: 0.625rem;
  align-items: center;
`;

const Email = styled.span`
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 2rem;
`;

const SettingsPhoto = styled.img`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  object-fit: cover;
`;

const LogOut = styled.div`
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 2rem;
  color: ${colors.text.link};
  cursor: pointer;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
`;

const GroupTitle = styled.span`
  font-size: 2rem;
  font-weight: 600;
  line-height: 2.5rem;
`;

const Description = styled.span`
  font-size: 1.25rem;
  font-weight: 400;
  line-height: 1.5rem;
  color: ${colors.text.subtitle};
`;

const BlockListTextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid ${colors.lines.link};
  border-radius: 0.75rem;
  background: ${colors.bg.primary};
  color: ${colors.text.primary};
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  min-height: 6rem;

  &:focus {
    outline: none;
    border-color: ${colors.text.link};
  }
`;

const Button = styled.button`
  background: ${colors.bg.primary};
  border: 1px solid ${colors.lines.link};
  border-radius: 0.75rem;
  padding: 1rem;
  cursor: pointer;
`;

interface ProfilePanelProps {
  isOpen: boolean;
}

export const ProfilePanel: React.FC<ProfilePanelProps> = ({ isOpen }) => {
  const { closePanel } = usePanelContext();
  const { profile, updateProfile } = useProfileContext();
  const { settings, setSetting } = useSettings();
  const navigate = useNavigate();
  const defaultBlockedHosts = (profile?.blockedHosts ?? []).join(', ');

  const [debouncedUpdateProfile, flushDebouncedUpdate] = useMemo(
    () =>
      debounceWithFlush((updatedProfile) => {
        updateProfile(updatedProfile);
      }, 500),
    [updateProfile],
  );

  // Group settings by section
  const settingsSections = useMemo(() => {
    const sectionMap = new Map<string, Array<{ path: SettingPath; metadata: SettingMetadata; value: unknown }>>();

    Object.entries(SETTINGS_CONFIG).forEach(([categoryKey, category]) => {
      Object.entries(category).forEach(([fieldKey, metadata]) => {
        // Handle nested objects like annie.tools
        if (typeof metadata === 'object' && metadata !== null && !('type' in metadata)) {
          Object.entries(metadata).forEach(([nestedFieldKey, nestedMetadata]) => {
            if (typeof nestedMetadata === 'object' && nestedMetadata !== null && 'section' in nestedMetadata) {
              const section = (nestedMetadata as SettingMetadata).section || 'General';
              const path = `${categoryKey}.${fieldKey}.${nestedFieldKey}` as SettingPath;
              const value = (settings as unknown as Record<string, unknown>)[categoryKey];
              const nestedValue = value
                ? ((value as Record<string, unknown>)[fieldKey] as Record<string, unknown>)?.[nestedFieldKey]
                : undefined;

              if (!sectionMap.has(section)) {
                sectionMap.set(section, []);
              }

              sectionMap.get(section)!.push({
                path,
                metadata: nestedMetadata as SettingMetadata,
                value: nestedValue,
              });
            }
          });
        } else if (typeof metadata === 'object' && metadata !== null && 'section' in metadata) {
          const section = (metadata as SettingMetadata).section || 'General';
          const path = `${categoryKey}.${fieldKey}` as SettingPath;
          const value = (settings as unknown as Record<string, unknown>)[categoryKey];
          const fieldValue = value ? (value as Record<string, unknown>)[fieldKey] : undefined;

          if (!sectionMap.has(section)) {
            sectionMap.set(section, []);
          }

          sectionMap.get(section)!.push({
            path,
            metadata: metadata as SettingMetadata,
            value: fieldValue,
          });
        }
      });
    });

    // Sort fields within each section by order
    sectionMap.forEach((fields) => {
      fields.sort((a, b) => (a.metadata.order ?? 999) - (b.metadata.order ?? 999));
    });

    return sectionMap;
  }, [settings]);

  const handleBlockListChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void | Promise<void> => {
    const newBlockedHosts = e.target.value.split(',').map((host) => host.trim());
    if (!profile || !profile.userId) return;
    debouncedUpdateProfile({ ...profile, blockedHosts: newBlockedHosts });
  };

  const handleClose = (): void => {
    flushDebouncedUpdate();
    closePanel('profile');
  };

  useEffect(() => {
    return () => {
      flushDebouncedUpdate();
    };
  }, [flushDebouncedUpdate]);

  const isDeveloper = profile?.email.endsWith('@annote.com');

  return (
    <>
      <Backdrop $isOpen={isOpen} onClick={handleClose} />
      <ProfilePanelWrapper $isOpen={isOpen}>
        <PanelHeader $side="left">
          <PanelToggleButton icon={Settings} onClick={handleClose} title="Close Settings" />
        </PanelHeader>
        <ProfileContainer>
          <Row>
            <Header>Settings</Header>
          </Row>
          <Row>
            <UserDetails>
              <EmailGroup>
                <SettingsPhoto src={userImgUrl} />
                <Email>{profile?.email ?? ''}</Email>
              </EmailGroup>
              <LogOut onClick={() => navigate('/logout')}>Log out</LogOut>
            </UserDetails>
          </Row>
          <Row>
            <Group>
              <GroupTitle>Block list</GroupTitle>
              <Description>List of websites where annote won't be used.</Description>
              <BlockListTextArea defaultValue={defaultBlockedHosts} onChange={handleBlockListChange} />
            </Group>
          </Row>
          {Array.from(settingsSections.entries()).map(([sectionName, fields]) => (
            <Row key={sectionName}>
              <Group>
                <GroupTitle>{sectionName}</GroupTitle>
                <div>
                  {fields.map(({ path, metadata, value }) => (
                    <ProfileSettingField
                      key={path}
                      metadata={metadata}
                      value={value}
                      onChange={(newValue): void => {
                        setSetting(path, newValue);
                      }}
                    />
                  ))}
                </div>
              </Group>
            </Row>
          ))}
          {isDeveloper && (
            <Row>
              <Group>
                <GroupTitle>For Developers Only</GroupTitle>
                <Description>Some tools here only developers can see and use</Description>
                <Button
                  onClick={() => {
                    throw new Error('This is a test error for Sentry');
                  }}
                >
                  Send Test Error to Sentry
                </Button>
              </Group>
            </Row>
          )}
        </ProfileContainer>
      </ProfilePanelWrapper>
    </>
  );
};
